
import React, { Dispatch, SetStateAction } from "react";
import {
    Flex,
    Box,
    Text,
    Tag,
    ButtonGroup,
    Button,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Divider,
    Textarea,
    Spinner,
} from "@chakra-ui/react";
import LoadingOverlay from "components/LoadingOverlay";
import _ from "lodash";
import { ApprovalTaskDetails, ProcessComment, ProcessCommentsRequest } from "redux/services/approvalProcessor/types";
import { ApprovalTaskStatues, EMPTY_FIELD } from "common";
import {
    constructFullName,
    dateFormat,
    numberWithCommas,
    formatMoney,
    safeParseJSON,
} from "utils";
import { useHistory } from "react-router-dom";
import { GetProfileResponse } from "redux/services/membership/types";

export interface ApprovalDetailsContentProps {
    isPaymentOpsRole?: boolean;
    isBusinessApproverRole?: boolean;
    isBUApproverRole?: boolean;

    getApprovalStatus: () => {
        value: ApprovalTaskStatues;
        label: string;
        data: {
            colorScheme: string;
        };
    } | undefined

    approverProcessed?: {
        approverFirstName: string;
        approverLastName: string;
        comment?: string;
    };
    approverRejected?: {
        approverFirstName: string;
        approverLastName: string;
        comment?: string;
    };
    approverApproved?: {
        approverFirstName: string;
        approverLastName: string;
        comment?: string;
    };
    buApprover?: {
        approverFirstName: string;
        approverLastName: string;
        comment?: string;
    };

    isEnableForChangeStatus?: boolean;
    firstButtonText: string;
    secondButtonText: string;
    setDialogState: React.Dispatch<
        React.SetStateAction<{ isOpen: boolean; type?: ApprovalTaskStatues }>
    >;

    isLoadingProcessComments: boolean;
    commentsData: ProcessComment[];
    isFetchingProcessComments: boolean;
    isLoadMoreComments: boolean;
    getProcessCommentsParamsRequest: ProcessCommentsRequest;
    setGetProcessCommentsParamsRequest: React.Dispatch<
        React.SetStateAction<ProcessCommentsRequest>
    >;

    createCommentLoading: boolean;
    commentValue?: string;
    createComment: (args: {
        orgToken?: string;
        approvalProcessId: string;
        comment: string;
        commentAuthorName: string;
    }) => void;

    orgToken?: string;
    approvalTask?: ApprovalTaskDetails;

    setSelectedProofDocument: Dispatch<SetStateAction<undefined>> | Dispatch<SetStateAction<string>>;
    setSelectedPayrollFile: Dispatch<SetStateAction<undefined>> | Dispatch<SetStateAction<string>>;
    ProfileData?: GetProfileResponse
    setCommentValue: (value: string) => void;
}

const ApprovalDetailsContent: React.FC<ApprovalDetailsContentProps> = ({
    isPaymentOpsRole,
    isBusinessApproverRole,
    isBUApproverRole,
    getApprovalStatus,
    approverProcessed,
    approverRejected,
    approverApproved,
    buApprover,
    isEnableForChangeStatus,
    firstButtonText,
    secondButtonText,
    setDialogState,
    isLoadingProcessComments,
    commentsData,
    isFetchingProcessComments,
    isLoadMoreComments,
    getProcessCommentsParamsRequest,
    setGetProcessCommentsParamsRequest,
    createCommentLoading,
    commentValue,
    createComment,
    orgToken,
    approvalTask,
    setSelectedProofDocument,
    setSelectedPayrollFile,
    ProfileData,
    setCommentValue,
}) => {
    const history = useHistory();
    const processVariables = approvalTask?.processVariables
        ? safeParseJSON(approvalTask.processVariables.overallInformation)
        : {};

    return (
        <Flex
            flexDir={["column", "column", "column", "row"]}
            justifyContent="space-between"
            gap="20px"
        >
            <Box
                minW={"300px"}
                maxW="640px"
                mb={["100px", "100px", "100px", "unset"]}
                flex={1}
            >
                <Flex alignItems={"center"} mb="20px">
                    <Text fontWeight={600} fontSize={"16px"}>
                        {isPaymentOpsRole && "Payment Ops"}
                        {isBusinessApproverRole && "Enterprise User - Checker"}
                        {isBUApproverRole && "Enterprise User - Approver"}
                    </Text>
                    <Tag
                        ml="12px"
                        size={"md"}
                        colorScheme={getApprovalStatus()?.data?.colorScheme}
                    >
                        {getApprovalStatus()?.label ?? EMPTY_FIELD}
                    </Tag>
                </Flex>

                <Flex flexDir="column" gap="10px" mb={(approverProcessed || approverRejected || approverApproved || approverApproved || buApprover) ? "20px" : "0px"}>
                    {approverApproved && (
                        <Alert
                            status="success"
                            borderRadius="10px"
                            data-testid="alert-approved"
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle>
                                    {constructFullName(
                                        approverApproved.approverFirstName,
                                        undefined,
                                        approverApproved.approverLastName
                                    )}{" "}
                                    approved this payment request
                                </AlertTitle>
                                {approverApproved?.comment && (
                                    <AlertDescription>
                                        {approverApproved.comment}
                                    </AlertDescription>
                                )}
                            </Box>
                        </Alert>
                    )}

                    {buApprover && (
                        <Alert
                            status="success"
                            borderRadius="10px"
                            data-testid="alert-approved"
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle>
                                    {constructFullName(
                                        buApprover.approverFirstName,
                                        undefined,
                                        buApprover.approverLastName
                                    )}{" "}
                                    approved this payment request
                                </AlertTitle>
                                {buApprover?.comment && (
                                    <AlertDescription>
                                        {buApprover.comment}
                                    </AlertDescription>
                                )}
                            </Box>
                        </Alert>
                    )}

                    {approverProcessed && (
                        <Alert
                            status="success"
                            borderRadius="10px"
                            data-testid="alert-processed"
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle>
                                    {constructFullName(
                                        approverProcessed.approverFirstName,
                                        undefined,
                                        approverProcessed.approverLastName
                                    )}{" "}
                                    processed this payment request
                                </AlertTitle>
                                {approverProcessed?.comment && (
                                    <AlertDescription>
                                        {approverProcessed.comment}
                                    </AlertDescription>
                                )}
                            </Box>
                        </Alert>
                    )}

                    {approverRejected && (
                        <Alert
                            status="error"
                            borderRadius="10px"
                            data-testid="alert-rejected"
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle>
                                    {constructFullName(
                                        approverRejected.approverFirstName,
                                        undefined,
                                        approverRejected.approverLastName
                                    )}{" "}
                                    rejected this payment request
                                </AlertTitle>
                                {approverRejected?.comment && (
                                    <AlertDescription>
                                        {approverRejected.comment}
                                    </AlertDescription>
                                )}
                            </Box>
                        </Alert>
                    )}
                </Flex>


                {isEnableForChangeStatus && (
                    <ButtonGroup variant="outline" spacing={0}>
                        <Button
                            data-testid="btn-processed"
                            borderRadius={"none"}
                            size={"sm"}
                            onClick={() =>
                                setDialogState({
                                    isOpen: true,
                                    type: ApprovalTaskStatues.APPROVED,
                                })
                            }
                        >
                            {firstButtonText}
                        </Button>
                        <Button
                            data-testid="btn-decline"
                            borderRadius={"none"}
                            size={"sm"}
                            onClick={() =>
                                setDialogState({
                                    isOpen: true,
                                    type: ApprovalTaskStatues.REJECTED,
                                })
                            }
                        >
                            {secondButtonText}
                        </Button>
                    </ButtonGroup>
                )}

                <Box p="20px" borderRadius="10px" borderWidth="1px" mt="20px">
                    {isLoadingProcessComments && (
                        <LoadingOverlay data-testid="comments-loading" />
                    )}
                    <Box maxHeight="420px" overflow="auto" px="20px" mx="-20px">
                        {_.unionBy(commentsData, "id").map((comment) => (
                            <Box
                                key={comment.id}
                                p="16px"
                                borderRadius="10px"
                                border="1px solid rgb(224, 224, 224)"
                                boxShadow={"0 4px 12px rgba(0,0,0,.1)"}
                                mb="12px"
                            >
                                <Flex align="center" justify="space-between" mb="8px">
                                    <Text fontSize={"14px"} fontWeight={600}>
                                        {dateFormat(comment.createdAt, true)}
                                    </Text>
                                    <Text fontSize={"14px"} fontWeight={600}>
                                        {comment.commentAuthorName}
                                    </Text>
                                </Flex>
                                <Text>{comment.comment}</Text>
                            </Box>
                        ))}
                    </Box>

                    {isLoadMoreComments && (
                        <Flex gap="12px" mb="12px" justify="end">
                            {isFetchingProcessComments && (
                                <Spinner size="xs" color="blue.500" />
                            )}

                            <Text
                                color="blue.500"
                                cursor="pointer"
                                fontSize="14px"
                                onClick={() =>
                                    setGetProcessCommentsParamsRequest({
                                        ...getProcessCommentsParamsRequest,
                                        pageNumber:
                                            getProcessCommentsParamsRequest.pageNumber + 1,
                                    })
                                }
                            >
                                Show more
                            </Text>
                        </Flex>
                    )}

                    <Box>
                        <Textarea
                            data-testid="comment-input"
                            fontSize="14px"
                            placeholder="Add a comment"
                            value={commentValue}
                            onChange={(e) => setCommentValue(e.target.value)}
                            isInvalid={!!(commentValue && commentValue?.length >= 250)}
                        />
                        {commentValue && commentValue.length >= 250 && (
                            <Text color="red.500" fontSize="12px" mt="8px">
                                Comment must be less than 250 characters
                            </Text>
                        )}
                        <Flex justify="end" mt="12px">
                            <Button
                                data-testid="submit-button"
                                colorScheme="blue"
                                disabled={createCommentLoading || !commentValue || commentValue.length >= 250}
                                onClick={() =>
                                    createComment({
                                        orgToken,
                                        approvalProcessId: approvalTask?.id ?? "",
                                        comment: commentValue ?? "",
                                        commentAuthorName: constructFullName(
                                            ProfileData?.data?.firstName,
                                            undefined,
                                            ProfileData?.data?.lastName
                                        ),
                                    })
                                }
                            >
                                Post Comment
                            </Button>
                        </Flex>
                    </Box>
                </Box>
            </Box>

            <Flex flexDir={"column"}>
                <Box mb="50px">
                    <Box mb="30px">
                        <Text
                            fontWeight={600}
                            fontSize="16px"
                            textTransform={"uppercase"}
                            mb="5px"
                        >
                            Requester information
                        </Text>
                        <Divider height={"1px"} borderColor="#D1D5DB" />
                    </Box>

                    <Flex justifyContent={"space-between"} alignItems="end">
                        <Box>
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Requester Name
                            </Text>
                            <Text>
                                {approvalTask?.requesterFirstName}{" "}
                                {approvalTask?.requesterLastName ?? EMPTY_FIELD}
                            </Text>
                        </Box>
                        <Box>
                            <Text fontSize={"14px"} mb={"10px"} fontWeight={600}>
                                Requester Email
                            </Text>
                            <Text>{approvalTask?.requesterEmail ?? EMPTY_FIELD}</Text>
                        </Box>
                        <Text
                            textDecoration={"underline"}
                            color={"blue"}
                            _hover={{ cursor: "pointer" }}
                            onClick={() => {
                                history.push(
                                    `/audit-activity?resourceOwnerId=${approvalTask?.requesterId}`
                                );
                            }}
                        >
                            View Activity
                        </Text>
                    </Flex>
                </Box>
                <Box>
                    <Box mb="30px">
                        <Text
                            fontWeight={600}
                            fontSize="16px"
                            textTransform={"uppercase"}
                            mb="5px"
                        >
                            Disbursement Information
                        </Text>
                        <Divider height={"1px"} borderColor="#D1D5DB" />
                    </Box>

                    <Flex flexDir={"row"} wrap="wrap" maxW={"650px"} gap="24px">
                        <Box minW="320px">
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Payroll File Name
                            </Text>
                            <Text
                                data-testid="download-payroll-file"
                                as="span"
                                color={"blue"}
                                _hover={{ cursor: "pointer" }}
                                onClick={() => {
                                    if (processVariables?.payrollDocumentId)
                                        setSelectedPayrollFile(
                                            processVariables?.payrollDocumentId
                                        );
                                }}
                            >
                                {processVariables?.payrollDocumentName ?? EMPTY_FIELD}
                            </Text>
                        </Box>

                        <Box>
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Total Employees
                            </Text>
                            <Text>
                                {processVariables?.totalItemsCount
                                    ? numberWithCommas(processVariables?.totalItemsCount)
                                    : EMPTY_FIELD}
                            </Text>
                        </Box>

                        <Box minW="320px">
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Proof of Prefund File Name
                            </Text>
                            <Text
                                data-testid="download-proof-of-prefund-file"
                                as="span"
                                color={
                                    processVariables?.relatedDocumentId ? "blue" : "unset"
                                }
                                _hover={{
                                    cursor: processVariables?.relatedDocumentId
                                        ? "pointer"
                                        : "unset",
                                }}
                                onClick={() =>
                                    setSelectedProofDocument(
                                        processVariables?.relatedDocumentId
                                    )
                                }
                            >
                                {processVariables?.relatedDocumentName ?? EMPTY_FIELD}
                            </Text>
                        </Box>

                        <Box minW="220px">
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Total Amount
                            </Text>
                            <Text>
                                {processVariables?.amount
                                    ? formatMoney(processVariables?.amount)
                                    : EMPTY_FIELD}
                            </Text>
                        </Box>
                        <Box>
                            <Text fontSize={"14px"} fontWeight={600} mb={"10px"}>
                                Disbursement Date
                            </Text>
                            <Text>
                                {processVariables?.disbursementDate ?? EMPTY_FIELD}
                            </Text>
                        </Box>
                    </Flex>
                </Box>
            </Flex>
        </Flex>
    );
};

export default ApprovalDetailsContent;